import { RefObject, useEffect, useRef } from "react";

import { focusFormControl, focusKeyboardAware } from "@vahak/core-ui/dist/methods/keyboardViewport";

import {
    getCompactSheetMountFocusDelayMs,
    shouldDeferCompactSheetAutoFocusToUserTap
} from "../methods/homepageV2CompactSheet";

type UseCompactSheetAutoFocusOptions = {
    enabled?: boolean;
    delay?: number;
    scrollIntoView?: boolean;
};

function shouldSkipCompactSheetAutoFocus(enabled: boolean, hasFocused: boolean, hasTarget: boolean) {
    return !enabled || hasFocused || !hasTarget || shouldDeferCompactSheetAutoFocusToUserTap();
}

/** Deferred focus for an input located by element id inside a compact bottom sheet. */
export function useCompactSheetAutoFocus(
    elementId: string | undefined,
    {
        enabled = true,
        delay = getCompactSheetMountFocusDelayMs(),
        scrollIntoView = true
    }: UseCompactSheetAutoFocusOptions = {}
) {
    const hasFocusedRef = useRef(false);

    useEffect(() => {
        if (shouldSkipCompactSheetAutoFocus(enabled, hasFocusedRef.current, Boolean(elementId))) {
            return;
        }

        hasFocusedRef.current = true;

        focusFormControl({
            candidateIds: [elementId!],
            delay,
            scrollIntoView
        });
    }, [delay, elementId, enabled, scrollIntoView]);
}

/** Deferred focus for the first matching input inside a container ref (OTP boxes, etc.). */
export function useCompactSheetRefAutoFocus(
    containerRef: RefObject<HTMLElement | null>,
    selector = 'input:not([type="hidden"])',
    {
        enabled = true,
        delay = getCompactSheetMountFocusDelayMs(),
        scrollIntoView = true
    }: UseCompactSheetAutoFocusOptions = {}
) {
    const hasFocusedRef = useRef(false);

    useEffect(() => {
        if (shouldSkipCompactSheetAutoFocus(enabled, hasFocusedRef.current, true)) {
            return;
        }

        hasFocusedRef.current = true;

        const timer = window.setTimeout(() => {
            const focusTarget = containerRef.current?.querySelector<HTMLElement>(selector);
            if (!focusTarget) {
                hasFocusedRef.current = false;
                return;
            }

            focusKeyboardAware(focusTarget, { scrollIntoView });
        }, delay);

        return () => window.clearTimeout(timer);
    }, [containerRef, delay, enabled, scrollIntoView, selector]);
}
